/**
 * The "Partner offers" section of a trip page (REQ-025), below the
 * destination's health requirements.
 *
 * Rendered only when there is something to show: no offers, a hidden
 * marketplace, an unknown residence or a failed request all leave the page
 * exactly as it was without the section.
 */
import {
  getPartnerOffers,
  partnerOffersQueryKey,
} from "@/services/api/marketplace";
import { useI18nStore } from "@/stores/i18nStore";
import { useQuery } from "@tanstack/react-query";
import { Tag } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";
import { PartnerOfferList } from "./PartnerOfferList";

interface PartnerOffersSectionProps {
  tripId: string;
}

export const PartnerOffersSection: React.FC<PartnerOffersSectionProps> = ({
  tripId,
}) => {
  const { t } = useI18nStore();
  const { data } = useQuery({
    queryKey: partnerOffersQueryKey(tripId),
    queryFn: () => getPartnerOffers(tripId),
    retry: false,
  });

  if (!data || data.length === 0) return null;

  return (
    <section
      className="border-t border-gray-200 pt-6"
      aria-labelledby="partner-offers-heading"
    >
      <h3
        id="partner-offers-heading"
        className="mb-2 flex items-center gap-2 text-lg font-semibold text-gray-900"
      >
        <Tag className="h-5 w-5 text-gray-500" />
        {t.marketplaceTitle}
      </h3>
      <p className="mb-4 text-sm text-gray-600">{t.marketplaceIntro}</p>
      <PartnerOfferList offers={data} tripId={tripId} />
      <Link
        to={`/traveler/travels/${encodeURIComponent(tripId)}/partner-offers`}
        className="mt-3 inline-block text-sm font-medium text-primary-600 hover:underline"
      >
        {t.marketplaceSeeAll}
      </Link>
    </section>
  );
};
